import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Skeleton } from "antd"
import SeasonMeetCard, { ISeasonMeetWithClubs } from "./SeasonMeetCard"
import { gen, IClubSeason, swimminglyApi } from "./utils"
import { updateSeasonMeetArrayListener } from "./MeetSchedulePageNew"

const ACTIVITY_FEED_REFETCH_EVENT = "swimmingly:activity-feed:refetch"

export default function InPersonMeetCreatedPreview({
  item,
  preferGenericMeetLookup = false,
  preferMeetInformationLookup = false,
}: {
  item: {
    clubId?: number | null
    meetId?: number | null
    seasonId?: number | null
    typeKey?: string | null
    context?: Record<string, any>
  }
  preferGenericMeetLookup?: boolean
  preferMeetInformationLookup?: boolean
}): JSX.Element | null {
  const sourceClubId = Number(item.clubId || item.context?.clubId || 0)
  const clubId = preferGenericMeetLookup ? 0 : sourceClubId
  const meetId = Number(item.meetId || item.context?.meetId || 0)
  const seasonId = Number(item.seasonId || item.context?.seasonId || 0)
  const [loading, setLoading] = useState(false)
  const [meet, setMeet] = useState<ISeasonMeetWithClubs | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [teamsExpanded, setTeamsExpanded] = useState(false)
  const [clubSeasonIds, setClubSeasonIds] = useState<number[]>([])

  const missingIds = !meetId

  const prioritizedClubSeasonIds = useMemo(() => {
    return (clubSeasons: IClubSeason[]) => {
      const exactSeasonMatches = clubSeasons.filter(
        (clubSeason) => seasonId && clubSeason.seasonId === seasonId,
      )
      const remainingClubSeasons = clubSeasons.filter(
        (clubSeason) => !seasonId || clubSeason.seasonId !== seasonId,
      )

      return exactSeasonMatches.concat(remainingClubSeasons).map(
        (clubSeason) => clubSeason.clubSeasonId,
      )
    }
  }, [seasonId])

  const loadMeetFromClubContext = useCallback(
    async (targetClubId: number) => {
      const seasonsResponse: any = await swimminglyApi.get(gen(`/api/getSeasonsForClub/${targetClubId}`))
      const clubSeasons: IClubSeason[] = seasonsResponse?.clubSeasons || []

      if (!Array.isArray(clubSeasons) || clubSeasons.length === 0) {
        throw new Error("No club seasons were available for this meet.")
      }

      const prioritizedIds = prioritizedClubSeasonIds(clubSeasons)

      setClubSeasonIds(prioritizedIds)

      let matchedMeet: ISeasonMeetWithClubs | null = null

      for (const clubSeasonId of prioritizedIds) {
        const response: any = await swimminglyApi.get(gen(`/api/getClubSeasonMeets/${clubSeasonId}`))
        const meets: ISeasonMeetWithClubs[] = Array.isArray(response?.meets) ? response.meets : []
        matchedMeet = meets.find((seasonMeet) => Number(seasonMeet.meet.meetId) === meetId) || null

        if (matchedMeet) {
          break
        }
      }

      return matchedMeet
    },
    [meetId, prioritizedClubSeasonIds],
  )

  const loadMeetPreview = useCallback(async () => {
    let mounted = true

    if (missingIds) {
      setMeet(null)
      setError(null)
      setLoading(false)
      setClubSeasonIds([])
      return () => {
        mounted = false
      }
    }

    const run = async () => {
      setLoading(true)
      setError(null)

      try {
        if (!clubId || preferMeetInformationLookup) {
          try {
            const meetInformationResponse: any = await swimminglyApi.get(
              gen(`/api/getMeetInformation/${meetId}/${sourceClubId || ""}`),
            )
            const meetInformation = meetInformationResponse?.meet
            const meetClubSeasons = Array.isArray(meetInformationResponse?.clubSeasons)
              ? meetInformationResponse.clubSeasons
              : []

            if (!meetInformation || meetClubSeasons.length === 0) {
              throw new Error("generic_lookup_failed")
            }

            const normalizedMeet: ISeasonMeetWithClubs = {
              meet: {
                ...meetInformation,
                meetId,
                meet_date:
                  meetInformation.meet_date || meetInformation.endDate || meetInformation.startDate,
                meetstatus:
                  meetInformation.meetstatus === null || meetInformation.meetstatus === undefined
                    ? null
                    : Number(meetInformation.meetstatus),
              },
              meetCourse:
                meetInformation.unit_of_measure === 1
                  ? "SCM"
                  : meetInformation.unit_of_measure === 2
                    ? "LCM"
                    : "SCY",
              virtualMeetCourse:
                meetInformation.virtualCourse === 1
                  ? "SCM"
                  : meetInformation.virtualCourse === 2
                    ? "LCM"
                    : meetInformation.isVirtualMeet
                      ? "SCY"
                      : null,
              isVirtualMeet: Boolean(meetInformation.isVirtualMeet),
              startdate: meetInformation.startDate || undefined,
              clubs: meetClubSeasons.map((clubSeason: any) => ({
                club: clubSeason.club,
                meetstatus:
                  clubSeason.entryStatusInMeet === -1 ? 0 : clubSeason.entryStatusInMeet,
                entriesCount: Number(clubSeason.entriesCount || 0),
                confirmLineup: Boolean(clubSeason.confirmLineup),
                heatSheetAccepted: Boolean(clubSeason.heatSheetAccepted),
              })),
            }

            if (mounted) {
              setMeet(normalizedMeet)
              setTeamsExpanded(normalizedMeet.clubs.length <= 4)
              setClubSeasonIds(
                meetClubSeasons
                  .map((clubSeason: any) => Number(clubSeason.clubSeasonId || 0))
                  .filter((clubSeasonId: number) => clubSeasonId > 0),
              )
            }

            return
          } catch (genericLookupError: any) {
            if (!sourceClubId) {
              throw new Error("Unable to load this swim meet preview.")
            }

            const matchedMeet = await loadMeetFromClubContext(sourceClubId)

            if (!mounted) {
              return
            }

            if (!matchedMeet) {
              throw new Error("Unable to load this swim meet preview.")
            }

            setMeet(matchedMeet)
            setTeamsExpanded(matchedMeet.clubs.length <= 4)
            return
          }
        }

        const matchedMeet = await loadMeetFromClubContext(clubId)

        if (!mounted) {
          return
        }

        if (!matchedMeet) {
          setMeet(null)
          setError("Unable to load this swim meet preview.")
          return
        }

        setMeet(matchedMeet)
        setTeamsExpanded(matchedMeet.clubs.length <= 4)
      } catch (err: any) {
        if (!mounted) {
          return
        }

        setMeet(null)
        setError(err?.message || "Unable to load this swim meet preview.")
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    await run()

    return () => {
      mounted = false
    }
  }, [clubId, loadMeetFromClubContext, meetId, missingIds, preferMeetInformationLookup, sourceClubId])

  useEffect(() => {
    const cleanupPromise = loadMeetPreview()

    return () => {
      cleanupPromise.then((cleanup) => cleanup?.())
    }
  }, [loadMeetPreview])

  useEffect(() => {
    if (clubSeasonIds.length === 0) {
      return
    }

    const handleRefresh = () => {
      void loadMeetPreview()
    }

    clubSeasonIds.forEach((clubSeasonId) => {
      updateSeasonMeetArrayListener.on(`updateSeasonMeets${clubSeasonId}`, handleRefresh)
    })
    window.addEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)

    return () => {
      clubSeasonIds.forEach((clubSeasonId) => {
        updateSeasonMeetArrayListener.removeListener(
          `updateSeasonMeets${clubSeasonId}`,
          handleRefresh,
        )
      })
      window.removeEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)
    }
  }, [clubSeasonIds, loadMeetPreview])

  if (missingIds) {
    return (
      <Alert
        showIcon
        type="info"
        title="Open this swim meet from the action button below."
      />
    )
  }

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />
  }

  if (error) {
    return <Alert showIcon type="info" title={error} />
  }

  if (!meet) {
    return null
  }

  return (
    <SeasonMeetCard
      meet={meet}
      viewMode="regular"
      teamsExpanded={teamsExpanded}
      onToggleTeams={() => setTeamsExpanded((current) => !current)}
      hidePublishLabelOnMobile={item.context?.activityFeedTypeKey === "in_person_meet_results_published" || item.typeKey === "in_person_meet_results_published"}
    />
  )
}